import React, { useState, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useLocation } from 'react-router';

interface ImageCropModalProps {
  image: any;
  images?: any[];
  onCropComplete: (croppedImage: any) => void;
  onClose: () => void;
  onCancel: () => void;
}

function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;
  const cropWidth = crop.width * scaleX;
  const cropHeight = crop.height * scaleY;

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );
}

export default function ImageCropModal({
  image,
  images,
  onCropComplete,
  onClose,
  onCancel
}: ImageCropModalProps) {
  const listImages = images ? images.map((img: any, i: number) => {
    return {
      key: i,
      image: typeof img === 'string' ? img : img.url,
      originalData: typeof img === 'string' ? null : img,
      isActive: i === 0 ? true : false
    }
  }) : [];

  const pathname = useLocation().pathname;
  const checkPathname = pathname.includes("create-deck") ||
    pathname.includes("edit-deck") ||
    pathname.includes("create-advertisement") ||
    pathname.includes("edit-advertisement");

  const [currentImage, setCurrentImage] = useState<string>(typeof image === 'string' ? image : (image?.url || ''));
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = React.useRef<HTMLImageElement>(null);
  const previewCanvasRef = React.useRef<HTMLCanvasElement>(null);

  const initializeCrop = useCallback((img: HTMLImageElement) => {
    const minDimension = Math.min(img.width, img.height);
    const cropSize = Math.min(minDimension, 1200);
    const cropX = (img.width - cropSize) / 2;
    const cropY = (img.height - cropSize) / 2;

    if (checkPathname) {
      setCrop({
        unit: 'px',
        x: cropX,
        y: cropY,
        width: 343,
        height: 128,
      });
    } else {
      setCrop({
        unit: 'px',
        x: cropX,
        y: cropY,
        width: cropSize,
        height: cropSize,
      });
    }
  }, [checkPathname]);

  const onLoad = useCallback((img: HTMLImageElement) => {
    initializeCrop(img);
  }, [initializeCrop]);

  const handleImageClick = (img: { key: number, image: string }, index: number) => {
    setCurrentImage(img.image);
    setActiveImageIndex(index);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  React.useEffect(() => {
    if (completedCrop && imgRef.current && previewCanvasRef.current) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop]);

  const handleCropComplete = () => {
    const selectedImg = listImages[activeImageIndex];
    if (selectedImg && selectedImg.originalData && selectedImg.originalData.type === 'cached') {
      onCropComplete(selectedImg.originalData);
      onClose();
      return;
    }

    if (!completedCrop || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    let quality = 0.9;

    const compressAndCheckSize = () => {
      canvas.toBlob((blob) => {
        if (!blob) return;

        if (blob.size <= 500 * 1024 || quality <= 0.1) {
          const reader = new FileReader();
          reader.onloadend = () => {
            onCropComplete(reader.result as string);
            onClose();
          };
          reader.readAsDataURL(blob);
        } else {
          quality -= 0.1;
          compressAndCheckSize();
        }
      }, 'image/jpeg', quality);
    };

    compressAndCheckSize();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Crop Image</h2>
          <button onClick={onClose} className="text-2xl">&times;</button>
        </div>

        {!checkPathname ? (
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            minWidth={200}
            minHeight={200}
            maxWidth={1200}
            maxHeight={1200}
            ruleOfThirds
            aspect={1}
          >
            <img
              ref={imgRef}
              src={currentImage}
              onLoad={(e) => onLoad(e.currentTarget)}
              style={{ maxHeight: '70vh' }}
            />
          </ReactCrop>
        ) : (
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            ruleOfThirds
            aspect={343 / 128}
          >
            <img
              ref={imgRef}
              src={currentImage}
              onLoad={(e) => onLoad(e.currentTarget)}
              style={{ maxHeight: '70vh' }}
            />
          </ReactCrop>
        )}

        {listImages.length !== 0 && (
          <div className="mb-4 flex flex-row flex-wrap gap-4">
            {listImages.map((img: { key: number, image: string }, i: number) => (
              <div
                key={i}
                onClick={() => handleImageClick(img, i)}
                className={`flex items-center justify-center border p-2 rounded cursor-pointer transition-all ${activeImageIndex === i
                    ? 'border-blue-500 border-4'
                    : 'border-black border-4'
                  }`}
              >
                <img
                  alt={`Image ${i + 1}`}
                  src={img.image}
                  className="w-[200px] h-[200px] object-contain"
                />
              </div>
            ))}
          </div>
        )}

        <div className="mt-4" hidden>
          <h3 className="font-semibold mb-2">Preview:</h3>
          <canvas
            ref={previewCanvasRef}
            className="border border-gray-300"
            style={{
              objectFit: 'contain',
              width: '200px',
              height: '200px',
            }}
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleCropComplete}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
}